import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import { Typography } from '@mui/material';

import DefaultNumber from 'src/components/common/DefaultNumber';
import Tooltip from 'src/components/common/Tooltip';
import GradientButton from 'src/components/GradientButton';
import IconButton from 'src/components/IconButton';
import TransactionBottomSheet from 'src/components/TransactionBottomSheet';
import { DYDX_TOKEN, USDC_TOKEN } from 'src/constants/tokens';
import { COSMOSTATION_OPERATOR_ADDRESS } from 'src/constants/validator';
import { useAccountSWR } from 'src/hooks/SWR/cosmos/useAccountSWR';
import { useCosmosBalanceSWR } from 'src/hooks/SWR/cosmos/useCosmosBalanceSWR';
import { useDelegationSWR } from 'src/hooks/SWR/cosmos/useDelegationSWR';
import { useRewardsSWR } from 'src/hooks/SWR/cosmos/useRewardsSWR';
import { useSimulateSWR } from 'src/hooks/SWR/cosmos/useSimulateSWR';
import { useTxInfoSWR } from 'src/hooks/SWR/cosmos/useTxInfoSWR';
import { useValidatorsSWR } from 'src/hooks/SWR/cosmos/useValidatorsSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { getSimulateWithdrawAllRewardTxBytes, getWithdrawAllRewardDirectDoc, signDirectTx } from 'src/utils/cosmos';
import { mnemonicToPair } from 'src/utils/crypto';
import { claimClickEvent, pageView } from 'src/utils/ga';
import { ceil, gt, minus, times, toDisplayDenomAmount } from 'src/utils/number';
import { isEqualsIgnoringCase } from 'src/utils/string';

import {
  Base02Text,
  Container,
  ContentsContainer,
  DetailContainer,
  Divider,
  DYDXContainer,
  Header,
  HeaderTitle,
  IconContainer,
  StyledBottomSheet,
  TokenContainer,
  TokenLeftColumnContainer,
  TokenLeftContainer,
  TokenRightContainer,
  WrapperContainer,
} from './styled';
import SignConfirmBottomSheet from '../../SignConfirmBottomSheet';
import TxDetails from '../../TxDetails';

import { ReactComponent as CloseIcon } from 'src/assets/svg/Close.svg';
import { ReactComponent as DYDXIcon } from 'src/assets/svg/DYDX.svg';
import { ReactComponent as USDCIcon } from 'src/assets/svg/USDC.svg';

type ClaimAllBottomSheetProps = Omit<React.ComponentProps<typeof StyledBottomSheet>, 'children'>;

export type RewardToken = {
  denom: string;
  amount: string;
  displayRewardAmount: string;
  token?: {
    name: string;
    symbol: string;
    decimals: number;
  };
};

export default function ClaimAllBottomSheet({ onClose, ...remainder }: ClaimAllBottomSheetProps) {
  const { enqueueSnackbar } = useSnackbar();

  const { currentAccount } = useCurrentAccount();
  const account = useAccountSWR(currentAccount.cosmosAddress);

  const [isDisabled, setIsDisabled] = useState(false);

  const [isOpenSignConfirmBottomsheet, setIsOpenSignConfirmBottomsheet] = useState(false);
  const [isOpenTrasactionBottomsheet, setIsOpenTrasactionBottomsheet] = useState(false);

  const [isTxTrackingFailed, setIsTxTrackingFailed] = useState(false);
  const [isTxTrackingSuccess, setIsTxTrackingSucces] = useState(false);

  const [claimedRewards, setClaimedRewards] = useState<RewardToken[]>();

  const [currentMemo, setCurrentMemo] = useState('');

  const [transactionHash, setTransactionHash] = useState('');

  const [debouncedCurrentMemo] = useDebounce(currentMemo, 500);
  const rewards = useRewardsSWR({ cosmosAddress: currentAccount.cosmosAddress });

  const validators = useValidatorsSWR();

  const delegation = useDelegationSWR({ cosmosAddress: currentAccount.cosmosAddress });

  const delegatedValidators = useMemo(
    () =>
      delegation.data?.delegation_responses
        ?.map((item) => {
          const rewardsData = rewards.data?.rewards?.find((reward) => isEqualsIgnoringCase(reward.validator_address, item.delegation.validator_address));
          const validatorData = validators.data?.validators.find((validator) =>
            isEqualsIgnoringCase(validator.operator_address, item.delegation.validator_address),
          );
          return {
            ...item,
            reward: rewardsData,
            validator: validatorData,
          };
        })
        .sort((a, b) => (gt(a.balance.amount, b.balance.amount) ? -1 : 1))
        .sort((item) => (isEqualsIgnoringCase(item.validator?.operator_address, COSMOSTATION_OPERATOR_ADDRESS) ? -1 : 1)),
    [delegation.data?.delegation_responses, rewards.data?.rewards, validators.data?.validators],
  );

  const mostRewardDelegatedValidator = useMemo(
    () => delegatedValidators?.sort((a, b) => (gt(a.balance.amount, b.balance.amount) ? -1 : 1))[0],
    [delegatedValidators],
  );

  const balance = useCosmosBalanceSWR(currentAccount.cosmosAddress);
  const baseCoinAmount = useMemo(() => balance.data?.balances?.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0', [balance.data?.balances]);

  const rewardTokens = useMemo(
    () =>
      rewards.data?.total?.map((item) => {
        const token = item.denom === DYDX_TOKEN.denom ? DYDX_TOKEN : item.denom === USDC_TOKEN.denom ? USDC_TOKEN : undefined;
        const displayRewardAmount = toDisplayDenomAmount(
          item.amount,
          item.denom === DYDX_TOKEN.denom ? DYDX_TOKEN.decimals : item.denom === USDC_TOKEN.denom ? USDC_TOKEN.decimals : 6,
        );

        return {
          ...item,
          displayRewardAmount,
          token,
        };
      }) || [],
    [rewards.data?.total],
  );

  const dydxRewardBaseAmount = useMemo(() => rewardTokens?.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0', [rewardTokens]);
  const usdcRewardBaseAmount = useMemo(() => rewardTokens?.find((item) => item.denom === USDC_TOKEN.denom)?.amount || '0', [rewardTokens]);

  const displayDYDXRewardBaseAmount = useMemo(() => toDisplayDenomAmount(dydxRewardBaseAmount, DYDX_TOKEN.decimals), [dydxRewardBaseAmount]);
  const displayUSDCRewardBaseAmount = useMemo(() => toDisplayDenomAmount(usdcRewardBaseAmount, USDC_TOKEN.decimals), [usdcRewardBaseAmount]);

  const simulateDirectTxBytes = useMemo(
    () =>
      account.data?.account.account_number && currentAccount.mnemonic && account.data?.account.sequence && rewards.data?.rewards && isOpenSignConfirmBottomsheet
        ? getSimulateWithdrawAllRewardTxBytes({
            accountNumber: account.data.account.account_number,
            sequence: account.data.account.sequence,
            mnemonic: currentAccount.mnemonic,
            rewards: rewards.data.rewards.map((item) => ({
              validatorAddress: item.validator_address,
              delegatorAddress: currentAccount.cosmosAddress,
            })),
            memo: debouncedCurrentMemo,
          })
        : undefined,
    [
      account.data?.account.account_number,
      account.data?.account.sequence,
      currentAccount.cosmosAddress,
      currentAccount.mnemonic,
      debouncedCurrentMemo,
      rewards.data?.rewards,
      isOpenSignConfirmBottomsheet,
    ],
  );

  const simulate = useSimulateSWR({ txBytes: simulateDirectTxBytes?.tx_bytes });

  const simulatedGas = useMemo(
    () => (simulate.data?.gas_info?.gas_used ? String(ceil(times(simulate.data.gas_info.gas_used, '1.2'))) : undefined),
    [simulate.data?.gas_info?.gas_used],
  );

  const currentGas = useMemo(() => simulatedGas || '250000', [simulatedGas]);

  const baseEstimateFeeAmount = useMemo(() => String(ceil(times(currentGas, '12500000000'))), [currentGas]);

  const displayEstimateFeeAmount = useMemo(() => toDisplayDenomAmount(baseEstimateFeeAmount, DYDX_TOKEN.decimals), [baseEstimateFeeAmount]);

  const withdrawRewardAllDirectTx = useMemo(
    () =>
      account.data?.account.account_number &&
      currentAccount.mnemonic &&
      account.data?.account.sequence &&
      currentAccount.cosmosAddress &&
      baseEstimateFeeAmount &&
      currentGas &&
      rewards.data?.rewards
        ? getWithdrawAllRewardDirectDoc({
            accountNumber: account.data.account.account_number,
            sequence: account.data.account.sequence,
            mnemonic: currentAccount.mnemonic,
            rewards: rewards.data.rewards.map((item) => ({
              validatorAddress: item.validator_address,
              delegatorAddress: currentAccount.cosmosAddress,
            })),
            simulatedGas: currentGas,
            simulatedFeeAmount: baseEstimateFeeAmount,
            memo: debouncedCurrentMemo,
          })
        : undefined,
    [
      account.data?.account.account_number,
      account.data?.account.sequence,
      baseEstimateFeeAmount,
      currentAccount.cosmosAddress,
      currentAccount.mnemonic,
      currentGas,
      debouncedCurrentMemo,
      rewards.data?.rewards,
    ],
  );
  const txInfo = useTxInfoSWR(transactionHash);

  const transactionBodyText = useMemo(() => {
    if (isTxTrackingFailed) return 'Rewards Claim Failed';

    if (isTxTrackingSuccess) return 'Successfully Claimed Rewards';

    return `Claiming${
      gt(claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, USDC_TOKEN.denom))?.amount || '0', '0')
        ? ` ${
            gt(claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, USDC_TOKEN.denom))?.displayRewardAmount || '0', '0.000001')
              ? claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, USDC_TOKEN.denom))?.displayRewardAmount || '0'
              : '< 0.000001'
          } USDC and `
        : ''
    } ${
      gt(claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, DYDX_TOKEN.denom))?.amount || '0', '0')
        ? gt(claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, DYDX_TOKEN.denom))?.displayRewardAmount || '0', '0.000001')
          ? claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, DYDX_TOKEN.denom))?.displayRewardAmount || '0'
          : '< 0.000001'
        : '0'
    } DYDX`;
  }, [claimedRewards, isTxTrackingFailed, isTxTrackingSuccess]);

  const transactionSubText = useMemo(
    () =>
      isTxTrackingSuccess
        ? `Claimed${
            gt(claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, USDC_TOKEN.denom))?.amount || '0', '0')
              ? ` ${
                  gt(claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, USDC_TOKEN.denom))?.displayRewardAmount || '0', '0.000001')
                    ? claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, USDC_TOKEN.denom))?.displayRewardAmount || '0'
                    : '< 0.000001'
                } USDC and `
              : ''
          } ${
            gt(claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, DYDX_TOKEN.denom))?.amount || '0', '0')
              ? gt(claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, DYDX_TOKEN.denom))?.displayRewardAmount || '0', '0.000001')
                ? claimedRewards?.find((item) => isEqualsIgnoringCase(item.denom, DYDX_TOKEN.denom))?.displayRewardAmount || '0'
                : '< 0.000001'
              : '0'
          } DYDX`
        : undefined,
    [claimedRewards, isTxTrackingSuccess],
  );

  const errorMessage = useMemo(() => {
    if (!currentAccount.cosmosAddress) {
      return 'Please connect wallet';
    }

    if (gt(baseEstimateFeeAmount, baseCoinAmount)) {
      return 'Insufficient DYDX balance for transaction fee';
    }

    if (!withdrawRewardAllDirectTx) {
      return 'Invalid Transaction';
    }

    if (!gt(usdcRewardBaseAmount, '0') && !gt(dydxRewardBaseAmount, '0')) {
      return 'There are no claimable rewards';
    }

    return '';
  }, [baseCoinAmount, baseEstimateFeeAmount, currentAccount.cosmosAddress, dydxRewardBaseAmount, usdcRewardBaseAmount, withdrawRewardAllDirectTx]);

  const txConfirmedStatus = useMemo(() => {
    if (txInfo.error?.response?.status && txInfo.error.response.status >= 400 && txInfo.error.response.status < 500) return 'pending';

    if (txInfo.data?.tx_response.code !== undefined) {
      if (txInfo.data.tx_response.code !== 0) return 'failed';

      if (txInfo.data.tx_response.code === 0) return 'confirmed';
    }

    return undefined;
  }, [txInfo.data?.tx_response.code, txInfo.error?.response?.status]);

  useEffect(() => {
    pageView('Claim-All', 'Claim-All');
  }, []);

  const debouncedEnabled = useDebouncedCallback(() => {
    setTimeout(() => {
      setIsDisabled(false);
    }, 700);
  }, 700);

  useEffect(() => {
    setIsDisabled(true);

    debouncedEnabled();
  }, [debouncedEnabled, withdrawRewardAllDirectTx, currentMemo]);

  useEffect(() => {
    if (isOpenTrasactionBottomsheet) {
      if (txConfirmedStatus === 'confirmed') {
        setIsTxTrackingSucces(true);
      }
      if (txConfirmedStatus === 'failed' || txInfo.hasTimedOut) {
        setIsTxTrackingFailed(true);
      }
    }
  }, [isOpenTrasactionBottomsheet, txConfirmedStatus, txInfo.hasTimedOut]);

  useEffect(() => {
    if (!currentAccount.cosmosAddress) {
      setIsOpenTrasactionBottomsheet(false);
      setIsTxTrackingFailed(false);
      setIsTxTrackingSucces(false);
      setTransactionHash('');
      setClaimedRewards(undefined);
      setCurrentMemo('');
      onClose?.({}, 'backdropClick');
    }
  }, [currentAccount.cosmosAddress, onClose]);

  return (
    <StyledBottomSheet {...remainder} onClose={onClose}>
      <Container>
        <>
          <ContentsContainer>
            <Header>
              <HeaderTitle>
                <Typography variant="h2">Claim All Rewards</Typography>
              </HeaderTitle>
              <IconButton
                onClick={() => {
                  onClose?.({}, 'backdropClick');
                }}
              >
                <CloseIcon />
              </IconButton>
            </Header>

            <DYDXContainer>
              <TokenContainer>
                <TokenLeftContainer>
                  <IconContainer>
                    <DYDXIcon />
                  </IconContainer>
                  <TokenLeftColumnContainer>
                    <Typography variant="h4">DYDX</Typography>
                    <Base02Text>
                      <Typography variant="h9">dYdX Chain Token</Typography>
                    </Base02Text>
                  </TokenLeftColumnContainer>
                </TokenLeftContainer>

                <TokenRightContainer>
                  <DefaultNumber typoOfIntegers="h4" typoOfDecimals="h6">
                    {displayDYDXRewardBaseAmount}
                  </DefaultNumber>
                </TokenRightContainer>
              </TokenContainer>

              {gt(usdcRewardBaseAmount, '0') && (
                <>
                  <Divider />

                  <TokenContainer>
                    <TokenLeftContainer>
                      <IconContainer>
                        <USDCIcon />
                      </IconContainer>
                      <TokenLeftColumnContainer>
                        <Typography variant="h4">USDC</Typography>
                        <Base02Text>
                          <Typography variant="h9">IBC Token</Typography>
                        </Base02Text>
                      </TokenLeftColumnContainer>
                    </TokenLeftContainer>

                    <TokenRightContainer>
                      <DefaultNumber typoOfIntegers="h4" typoOfDecimals="h6">
                        {displayUSDCRewardBaseAmount}
                      </DefaultNumber>
                    </TokenRightContainer>
                  </TokenContainer>
                </>
              )}
            </DYDXContainer>

            <DetailContainer>
              <TxDetails estimatedGasFee={displayEstimateFeeAmount} isOnlyEstimatedGasFee />
            </DetailContainer>
          </ContentsContainer>
          <Tooltip varient="error" title={errorMessage} placement="top" arrow>
            <div>
              <GradientButton
                disabled={!!errorMessage || !withdrawRewardAllDirectTx}
                onClick={() => {
                  setIsOpenSignConfirmBottomsheet(true);
                }}
              >
                <Typography variant="h4">NEXT</Typography>
              </GradientButton>
            </div>
          </Tooltip>
        </>
      </Container>

      <WrapperContainer data-is-open={isOpenSignConfirmBottomsheet}>
        <SignConfirmBottomSheet
          open={isOpenSignConfirmBottomsheet}
          currentValidator={mostRewardDelegatedValidator?.validator}
          variant="persistent"
          title="Confirm Claiming Details"
          descriptionText={`Review your rewards claim transaction and click 'Confirm' to proceed.`}
          delegateAmount={toDisplayDenomAmount(rewardTokens?.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0', DYDX_TOKEN.decimals)}
          additionalAmount={rewardTokens
            ?.filter((item) => item.denom !== DYDX_TOKEN.denom)
            .map((item) => ({
              denom: item.denom,
              amount: item.amount,
            }))}
          transitionDuration={0}
          additionalValidatorCount={minus(delegatedValidators?.length || '0', '1')}
          amountTitle="Total Reward"
          displayEstimatedGasFeeAmount={displayEstimateFeeAmount}
          currentMemo={currentMemo}
          isButtonDisabled={isDisabled}
          isEstimation={!simulatedGas && !simulate.error}
          setCurrentMemo={(memo) => {
            setCurrentMemo(memo);
          }}
          onClickConfirm={async () => {
            try {
              if (!withdrawRewardAllDirectTx || !account.data?.account.account_number) return;
              setIsOpenSignConfirmBottomsheet(false);
              setIsOpenTrasactionBottomsheet(true);
              setClaimedRewards(rewardTokens);

              const keypair = mnemonicToPair(currentAccount.mnemonic);
              const response = await signDirectTx(withdrawRewardAllDirectTx, keypair, account.data.account.account_number);

              claimClickEvent('Claim-All', 'Claim-All');

              setTransactionHash(response.tx_response.txhash);

              enqueueSnackbar('Transaction broadcasting success', {
                variant: 'success',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                txHash: response.tx_response.txhash,
                isCosmosTx: true,
              });

              await txInfo.mutate();
            } catch (e) {
              setIsTxTrackingFailed(true);

              enqueueSnackbar('Transaction Failed', {
                variant: 'error',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                errorMsg: '',

                autoHideDuration: 3000,
              });
            }
          }}
          onClose={() => {
            setIsOpenSignConfirmBottomsheet(false);
            setCurrentMemo('');
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenTrasactionBottomsheet}>
        <TransactionBottomSheet
          open={isOpenTrasactionBottomsheet}
          variant="persistent"
          title="Claiming Status"
          bodyText={transactionBodyText}
          subText={transactionSubText}
          // redirectButtonText={transactionRedirectText}
          isFailed={isTxTrackingFailed}
          isSuccess={isTxTrackingSuccess}
          isStaking
          txHash={transactionHash}
          transitionDuration={0}
          onClickRedirect={() => {
            window.open('https://medium.com/cosmostation/dydx-wallet-migration-guide-how-to-manage-your-dydx-tokens-43982ffddfb5');
          }}
          onClose={() => {
            setIsOpenTrasactionBottomsheet(false);
            setIsTxTrackingFailed(false);
            setIsTxTrackingSucces(false);
            setTransactionHash('');
            void rewards.mutate();
            setClaimedRewards(undefined);

            setCurrentMemo('');
            onClose?.({}, 'backdropClick');
          }}
        />
      </WrapperContainer>
    </StyledBottomSheet>
  );
}
